import { useAuthStore } from "@/stores/userAuth";
import handleError from "@/utils/handleError";

const ORDER_NUMBER_FIELD_ID = "4796771910431";
const COMPANY_NAME_FIELD_ID = "4819480471967";
const CONTRACT_NUMBER_FIELD_ID = "4822912572063";
const ORDER_LINK_FIELD_ID = "4822949575071";

export const useZendesk = () => {
    const authStore = useAuthStore();

    const createZendeskChatWidgetScript = () => {
        const chatWidgetKey = authStore.companyData?.zendesk_chat_widget_key;
        if (!chatWidgetKey) return Promise.resolve();

        return new Promise<void>((resolve) => {
            const script = document.createElement("script");
            script.id = "ze-snippet";
            script.src = `https://static.zdassets.com/ekr/snippet.js?key=${chatWidgetKey}`;
            script.async = true;
            script.onload = () => {
                if (window.zE) {
                    resolve();
                }
            };
            document.head.appendChild(script);
        });
    };

    const setConversationFields = (
        fields: Array<{ id: string; value: string | number }>
    ) => {
        if (window.zE) {
            window.zE("messenger:set", "conversationFields", fields);
        }
    };

    const clearAllFields = () => {
        setConversationFields([
            {
                id: ORDER_NUMBER_FIELD_ID,
                value: "",
            },
            {
                id: ORDER_LINK_FIELD_ID,
                value: "",
            },
            {
                id: CONTRACT_NUMBER_FIELD_ID,
                value: "",
            },
        ]);
    };

    const setOrderNumberField = (orderNumber: string, orderUuid: string) => {
        clearAllFields();
        const orderLink = `${window.location.origin}/orders/${orderUuid}`;
        setConversationFields([
            {
                id: ORDER_NUMBER_FIELD_ID,
                value: orderNumber,
            },
            {
                id: ORDER_LINK_FIELD_ID,
                value: orderLink,
            },
        ]);
    };

    const setContractNumberField = (contractNumber: string) => {
        clearAllFields();
        setConversationFields([
            {
                id: CONTRACT_NUMBER_FIELD_ID,
                value: contractNumber,
            },
        ]);
    };

    const setCompanyField = (companyName: string) => {
        setConversationFields([
            {
                id: COMPANY_NAME_FIELD_ID,
                value: companyName,
            },
        ]);
    };

    const loginToZendesk = () => {
        if (
            window.zE &&
            authStore.companyData?.zendesk_chat_widget_key &&
            authStore.companyData?.zendesk_chat_token
        ) {
            window.zE(
                "messenger",
                "loginUser",
                (callback: any) => {
                    const jwt = authStore.companyData?.zendesk_chat_token;
                    callback(jwt);
                },
                (error: any) => {
                    if (error) {
                        handleError(error);
                    }
                }
            );
        }
    };

    return {
        setConversationFields,
        setOrderNumberField,
        setCompanyField,
        loginToZendesk,
        createZendeskChatWidgetScript,
        setContractNumberField,
    };
};
